import React, { useEffect, useState } from 'react';
import Sidebar from '../../../../component/b2c/sidebar/sidebar';
import NavBar from '../../../../component/b2c/navBar/navBar';
import Pagination from '@mui/material/Pagination';
import { useMyContext } from '../../../../context/MyContext';
import { FaCcVisa, FaCcMastercard } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PaginationItem from '@mui/material/PaginationItem';
import { Base_Url, shippingCharges, shippingUserCharges } from '../../../../config/config';
import FullPageLoader from '../../../../component/fullPageLoader/fullPageLoader';
import { Download } from 'lucide-react';

const TopupHistory = () => {
  const [isPageLoading, setIsPageLoading] = useState(false)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const navigate = useNavigate();
  const [historyData, setHistoryData] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const token = localStorage.getItem('token');
  const cus_id = localStorage.getItem('cus_id');
  const role = localStorage.getItem('role');

  useEffect(() => {
    getShipmentCharges();
  }, [page, limit]);

  const handleNavigation = () => {
    navigate("/dashboard");
  };

  const getShipmentCharges = async () => {
    const apiEndpoint = shippingCharges;
    setIsPageLoading(true);
    const url = `${Base_Url}${apiEndpoint}?page=${page}&limit=${limit}`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          token: token,
          cus_id: cus_id,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.status === "SUCCESS") {
        setHistoryData(data.data);
        setTotalOrders(data.pagination.totalItems || 0);
        setIsPageLoading(false);
      } else {
        toast.error(data.message);
        setIsPageLoading(false);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsPageLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    if (historyData.length === 0) {
      toast.error("No data available to download.");
      return;
    }

    const headers = ['Order ID', 'User ID', 'Amount', 'Last Balance', 'Transaction Status', 'Generate Date', 'Description'];
    const csvRows = [headers.join(',')];

    historyData.forEach(row => {
      const amount = row.credit_amount === "0" || row.credit_amount === null ? row.debit_amount : row.credit_amount;
      const rowData = [
        row.order_id,
        row.user_id,
        `${amount}`,
        `${row.closing_balance || 'N/A'}`,
        row.status === "add" ? row.refund_status : row.status,
        row.txn_date,
        `"${row.txn_description || 'N/A'}"`
      ];
      csvRows.push(rowData.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "shipment_charges.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePaginationChange = (event, value) => {
    setPage(value);
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
    setPage(1);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      setSelectedRows(historyData.map((_, index) => index));
    }
    setSelectAll(!selectAll);
  };

  const handleRowSelect = (index) => {
    if (selectedRows.includes(index)) {
      setSelectedRows(selectedRows.filter(i => i !== index));
    } else {
      setSelectedRows([...selectedRows, index]);
    }
  };

  const renderTableHeaders = () => {
    const headers = ['Order ID', 'Amount', 'Last Balance', 'Transaction Status', 'Generate Date', 'Description'];

    if (role === 'admin') {
      headers.unshift('User ID');
    }

    return headers;
  };

  // ✅ Neon status styling for dark mode
  const getStatusStyle = (status, refundStatus) => {
    if (isDarkMode) {
      if (status === "add") {
        switch (refundStatus?.toLowerCase()) {
          case 'completed':
            return 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40 shadow-[0_0_10px_rgba(16,185,129,0.2)]';
          case 'pending':
            return 'bg-amber-500/10 text-amber-300 border-amber-500/40 shadow-[0_0_10px_rgba(245,158,11,0.2)]';
          case 'failed':
            return 'bg-rose-500/10 text-rose-300 border-rose-500/40 shadow-[0_0_10px_rgba(244,63,94,0.2)]';
          default:
            return 'bg-gray-500/10 text-gray-400 border-gray-600/40';
        }
      } else {
        return 'bg-indigo-500/10 text-indigo-300 border-indigo-500/30';
      }
    } else {
      // Light mode preserved
      if (status === "add") {
        switch (refundStatus?.toLowerCase()) {
          case 'completed':
            return 'border-black/30 text-black';
          case 'pending':
            return 'border-black/20 text-gray-600';
          case 'failed':
            return 'border-black/40 text-black';
          default:
            return 'border-black/20 text-gray-600';
        }
      } else {
        return 'border-black/15 text-gray-500';
      }
    }
  };

  const getStatusDot = (status, refundStatus) => {
    if (isDarkMode) {
      if (status === "add") {
        switch (refundStatus?.toLowerCase()) {
          case 'completed':
            return 'bg-emerald-400 animate-pulse';
          case 'pending':
            return 'bg-amber-400 animate-pulse';
          case 'failed':
            return 'bg-rose-400';
          default:
            return 'bg-gray-500';
        }
      }
      return 'bg-indigo-400';
    }
    return 'bg-gray-400';
  };

  const renderTableRow = (row, index) => (
    <tr
      key={row.id}
      className={`transition-colors duration-150 ${
        isDarkMode
          ? index % 2 === 0 ? 'bg-[#111827] hover:bg-[#1F2937]' : 'bg-[#0F172A] hover:bg-[#1F2937]'
          : index % 2 === 0 ? 'bg-white hover:bg-black/[0.02]' : 'bg-black/[0.01] hover:bg-black/[0.02]'
      }`}
    >
      {role === 'admin' && (
        <td className={`p-2.5 text-[11px] font-medium ${
          isDarkMode ? 'text-gray-200' : 'text-gray-700'
        }`}>
          {row.user_id}
        </td>
      )}
      <td className={`p-2.5 text-[11px] font-semibold ${
        isDarkMode ? 'text-indigo-300' : 'text-black'
      }`}>
        {row.order_id}
      </td>
      <td className={`p-2.5 text-[11px] font-bold ${
        isDarkMode ? 'text-emerald-400' : 'text-gray-900'
      }`}>
        ₹{row.credit_amount === "0" || row.credit_amount === null ? row.debit_amount : row.credit_amount}
      </td>
      <td className={`p-2.5 text-[11px] ${
        isDarkMode ? 'text-gray-300' : 'text-gray-600'
      }`}>
        ₹{row.closing_balance || 'N/A'}
      </td>
      <td className="p-2.5">
        {row.status === "add" ? (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${getStatusStyle(row.status, row.refund_status)}`}>
            <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${getStatusDot(row.status, row.refund_status)}`}></span>
            {row.refund_status}
          </span>
        ) : (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${getStatusStyle(row.status)}`}>
            <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${getStatusDot(row.status)}`}></span>
            {row.status}
          </span>
        )}
      </td>
      <td className={`p-2.5 text-[11px] ${
        isDarkMode ? 'text-gray-400' : 'text-gray-600'
      }`}>
        {row.txn_date || 'N/A'}
      </td>
      <td className={`p-2.5 text-[11px] max-w-xs ${
        isDarkMode ? 'text-gray-400' : 'text-gray-600'
      }`}>
        <div
          className="truncate hover:text-clip hover:whitespace-normal cursor-help"
          title={row.txn_description || "No description available"}
        >
          {row.txn_description || "N/A"}
        </div>
      </td>
    </tr>
  );

  const glass =
    "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
    "border border-white/60 dark:border-white/10 " +
    "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";

  return (
    <div className={`h-screen flex flex-col overflow-hidden transition-colors duration-300 ${
      isDarkMode ? 'dark bg-[#0B1120]' : 'bg-white'
    }`}>
      {/* Premium ambient glows */}
      {isDarkMode ? (
        <>
          <div className="pointer-events-none absolute -top-40 -left-32 h-[500px] w-[500px] rounded-full bg-indigo-600/15 blur-[120px]" />
          <div className="pointer-events-none absolute bottom-0 -right-32 h-[400px] w-[400px] rounded-full bg-purple-600/10 blur-[100px]" />
        </>
      ) : (
        <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-200/30 blur-3xl" />
      )}

      <NavBar />
      <div className="flex flex-1 overflow-hidden relative z-10">
                            <div
            className={`
              ${isToggleSidebar ? "w-64" : "w-[72px]"}
              m-3
              rounded-3xl
              ${glass}
              overflow-hidden
              transition-all
              duration-300
              ease-in-out
            `}
          >
            <Sidebar />
          </div>

        <FullPageLoader
          isLoading={isPageLoading}
          message="Processing your request..."
        />

        <div className={`w-full flex-1 p-3 overflow-hidden flex flex-col border transition-colors duration-300 ${
          isDarkMode ? 'bg-[#0B1120] text-white border-transparent' : 'bg-gray-200 border-black/10'
        }`}>

          {/* Premium Header Buttons */}
          <div className="flex flex-col sm:flex-row items-center gap-2 mb-3 w-full flex-shrink-0">
            <button
              onClick={handleNavigation}
              className={`flex-1 w-full sm:w-auto text-xs font-bold rounded-xl h-10 flex items-center justify-center transition-all duration-300 ${
                isDarkMode
                  ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white shadow-[0_0_25px_rgba(99,102,241,0.3)] hover:shadow-[0_0_40px_rgba(99,102,241,0.5)]'
                  : 'bg-blue-600 text-white hover:bg-gray-800'
              }`}
            >
              <FaCcVisa className="mr-1.5 flex-shrink-0" size={14} />
              <FaCcMastercard className="mr-1.5 flex-shrink-0" size={14} />
              <span className="whitespace-nowrap">Shipments Charges</span>
            </button>

            <button
              onClick={handleDownloadCSV}
              className={`w-full sm:w-auto text-xs font-bold h-10 px-4 rounded-xl flex items-center justify-center transition-all duration-300 border ${
                isDarkMode
                  ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white border-transparent hover:shadow-[0_0_20px_rgba(16,185,129,0.5)]'
                  : 'border-black/20 text-black hover:bg-black/5'
              }`}
            >
              <Download className="mr-1.5 w-3.5 h-3.5" />
              <span className="whitespace-nowrap">Download CSV</span>
            </button>
          </div>

          {/* Premium Table */}
          <div className={`overflow-auto custom-scrollbar rounded-xl border flex-1 min-h-0 transition-colors duration-300 ${
            isDarkMode
              ? 'border-gray-800 bg-[#111827] shadow-[0_0_30px_rgba(0,0,0,0.4)]'
              : 'border-black/10'
          }`}>
            <table className="min-w-full table-auto">
              <thead className={`text-[10px] uppercase tracking-wider sticky top-0 z-10 transition-colors duration-300 ${
                isDarkMode
                  ? 'bg-gradient-to-r from-[#1F2937] via-[#1a2332] to-[#1F2937] text-gray-300 border-b border-gray-700'
                  : 'bg-blue-600 text-white'
              }`}>
                <tr>
                  {renderTableHeaders().map((header, index) => (
                    <th key={index} className="p-2.5 text-left font-semibold">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y transition-colors duration-300 ${
                isDarkMode ? 'divide-gray-800' : 'divide-gray-100'
              }`}>
                {historyData.length > 0 ? historyData.map((row, index) => renderTableRow(row, index)) : (
                  <tr>
                    <td colSpan={role === 'admin' ? "8" : "7"} className={`text-center p-6 text-[11px] ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                      No shipping charges history available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Premium Footer */}
          <div className="mt-3 flex flex-col sm:flex-row justify-between items-center gap-2 flex-shrink-0">
            <span className={`text-[11px] font-bold ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Total Orders: <span className={isDarkMode ? 'text-indigo-300' : ''}>{totalOrders}</span>
            </span>

            <select
              id="limit"
              value={limit}
              onChange={handleLimitChange}
              className={`rounded-lg p-1.5 text-[11px] focus:outline-none transition-all duration-300 ${
                isDarkMode
                  ? 'bg-[#1F2937] border border-gray-700 text-gray-200 focus:ring-1 focus:ring-indigo-500'
                  : 'border border-black/20 bg-white text-gray-700'
              } ${totalOrders < 10 ? 'cursor-not-allowed opacity-40' : ''}`}
              disabled={totalOrders < 10}
            >
              {totalOrders === 0 ? (
                <option value={0}>0</option>
              ) : (
                <>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={totalOrders}>All</option>
                </>
              )}
            </select>

            <Pagination
              count={Math.ceil(totalOrders / limit)}
              page={page}
              onChange={handlePaginationChange}
              variant="outlined"
              shape="rounded"
              className="pagination"
              size="small"
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  sx={{
                    borderRadius: '8px',
                    color: isDarkMode ? '#E5E7EB' : '#374151',
                    borderColor: isDarkMode ? '#374151' : '#d1d5db',
                    '&.Mui-selected': {
                      backgroundColor: isDarkMode ? 'rgba(99,102,241,0.2)' : '#000',
                      color: isDarkMode ? '#a5b4fc' : '#fff',
                      borderColor: isDarkMode ? '#6366f1' : '#000',
                      boxShadow: isDarkMode ? '0 0 10px rgba(99,102,241,0.4)' : 'none',
                    },
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(99,102,241,0.1)' : '#F3F4F6',
                    }
                  }}
                />
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopupHistory;